import { z } from "zod"
import {
  Agent,
  clampOutputPresentationMaxItems,
  DEFAULT_AGENT_RESPONSE_MODE,
  DEFAULT_HUMAN_HANDOFF_CONFIG,
  DEFAULT_OUTPUT_PRESENTATION_POLICY,
  MAX_AI_TAKEOVER_GREETING_LENGTH,
  OUTPUT_PRESENTATION_KINDS,
} from "@/types"
import type {
  DraftPolicy,
  OutputPresentationKind,
  OutputPresentationPolicy,
} from "@/types"
import { UploadingFile } from "@/components/Conversation/FileUploadTypes"
import type { KnowledgeBaseFolder } from "@/hooks/api"
import type { HttpRequestToolConfig } from "@/types/httpRequestTool"
import type { RequestFormSchema } from "@/types/requestForm"
import type { Text2SpeechConfig } from "@/types/text2speechTool"

export const MAX_ATTACHMENTS = 5
// IMPORTANT: Keep in sync with codeer/agents/schemas.py validate_suggested_questions
export const MAX_SUGGESTED_QUESTIONS = 3
export const MAX_SUGGESTED_QUESTION_LENGTH = 200
export const MAX_PRESENTATION_GUIDANCE_LENGTH = 800

const HumanHandoffSchema = z.object({
  enabled: z.boolean().default(false),
  idleTimeoutMinutes: z.number().int().positive().nullable().optional(),
  handoffInstructions: z.string().optional(),
})

const OutputPresentationKindSchema = z.enum([
  "citation_list",
  "card_carousel",
  "image_gallery",
  "action_panel",
  "list_digest",
  "status_timeline",
  "location_carousel",
])

const OutputPresentationPolicySchema = z
  .object({
    enableOutputPresentations: z
      .boolean()
      .default(DEFAULT_OUTPUT_PRESENTATION_POLICY.enableOutputPresentations),
    allowedPresentationKinds: z.array(OutputPresentationKindSchema).default([]),
    maxItemsByKind: z
      .record(OutputPresentationKindSchema, z.number().int().min(1))
      .default({}),
    whenToUseByKind: z
      .record(
        OutputPresentationKindSchema,
        z.string().max(MAX_PRESENTATION_GUIDANCE_LENGTH)
      )
      .default({}),
  })
  .default(DEFAULT_OUTPUT_PRESENTATION_POLICY)

// Unified Tool Types
export type UnifiedToolType =
  | "knowledge_base"
  | "web_search"
  | "call_agent"
  | "image_generation"
  | "request_form"
  | "payment"
  | "memory"
  | "http_request"
  | "text2speech"
  | "tabular_data"

export interface UnifiedTool {
  id: string
  type: UnifiedToolType
  name?: string
  description?: string
  version?: number
  domain?: string
  invocationInstruction?: string
  draftPolicy?: DraftPolicy
  agentId?: string
  knowledgeNodeIds?: string[]
  knowledgeNodeId?: string
  imageModel?: string
  // Request-form specific payloads (optional; depend on tool type)
  customFormSchema?: RequestFormSchema
  httpRequest?: HttpRequestToolConfig
  text2speech?: Text2SpeechConfig
}

export interface UnifiedToolSelectorProps {
  knowledgeBases: KnowledgeBaseFolder[]
  agents: Agent[]
  selectedTools: UnifiedTool[]
  onToolsChange: (tools: UnifiedTool[]) => void
  disabled?: boolean
  pendingKnowledgeBindings?: import("@/hooks/api/agents").PendingKnowledgeBindingSchema[]
  onPendingKnowledgeBindingsChange?: (
    bindings: import("@/hooks/api/agents").PendingKnowledgeBindingSchema[]
  ) => void
  onTabularToolsEnabledChange?: (enabled: boolean) => void
}

export const BasicFormSchema = z.object({
  name: z.string().min(1).max(256),
  description: z.string().max(512),
  systemPrompt: z.string(),
  llmModel: z.string().optional(),
  llmModelSettings: z
    .record(
      z.string(),
      z
        .object({
          temperature: z.number().min(0).max(2).optional(),
          maxTokens: z.number().int().positive().optional(),
          topP: z.number().gt(0).max(1).optional(),
          thinking: z
            .enum(["minimal", "low", "medium", "high", "xhigh"])
            .optional(),
        })
        .refine(
          (value) =>
            !(value.temperature !== undefined && value.topP !== undefined),
          {
            message: "temperature and top_p cannot be set together",
          }
        )
    )
    .default({}),
  attachments: z.array(z.custom<UploadingFile>()).max(MAX_ATTACHMENTS),
  unifiedTools: z.array(z.custom<UnifiedTool>()),
  knowledgeNodeIds: z.array(z.string()).default([]),
  suggestedQuestions: z
    .array(z.string().max(MAX_SUGGESTED_QUESTION_LENGTH))
    .max(MAX_SUGGESTED_QUESTIONS)
    .default([]),
  responseMode: z
    .enum(["ai", "human_only"])
    .default(DEFAULT_AGENT_RESPONSE_MODE),
  humanHandoff: HumanHandoffSchema.default(DEFAULT_HUMAN_HANDOFF_CONFIG),
  // Optional; blank disables the greeting. Sent verbatim, so it is not trimmed.
  aiTakeoverGreeting: z
    .string()
    .max(MAX_AI_TAKEOVER_GREETING_LENGTH, {
      message: "workspace.agent.basicForm.aiTakeoverGreeting.tooLong",
    })
    .default(""),
  outputPresentationPolicy: OutputPresentationPolicySchema,
  pendingKnowledgeBindings: z
    .array(
      z.custom<import("@/hooks/api/agents").PendingKnowledgeBindingSchema>()
    )
    .default([]),
})

export type BasicFormType = z.infer<typeof BasicFormSchema>

export const normalizeOutputPresentationPolicy = (
  policy?: Partial<OutputPresentationPolicy>
) => {
  if (!policy) return { ...DEFAULT_OUTPUT_PRESENTATION_POLICY }

  const knownKinds = new Set<string>(
    OUTPUT_PRESENTATION_KINDS.map(({ kind }) => kind)
  )
  const allowedPresentationKinds = (
    policy.allowedPresentationKinds || []
  ).filter((kind): kind is OutputPresentationKind => knownKinds.has(kind))

  return {
    enableOutputPresentations: policy.enableOutputPresentations ?? false,
    allowedPresentationKinds,
    maxItemsByKind: Object.fromEntries(
      Object.entries(policy.maxItemsByKind || {})
        .filter(([kind]) => knownKinds.has(kind))
        .map(([kind, maxItems]) => [
          kind,
          clampOutputPresentationMaxItems(
            kind as OutputPresentationKind,
            maxItems
          ),
        ])
    ),
    whenToUseByKind: Object.fromEntries(
      Object.entries(policy.whenToUseByKind || {})
        .filter(
          ([kind, guidance]) => knownKinds.has(kind) && !!guidance?.trim()
        )
        .map(([kind, guidance]) => [kind, guidance.trim()])
    ),
  }
}
